/**
 * Formats a Date object or a date string into a standard date format (DD/MM/YYYY).
 *
 * @function
 * @param {Date | string} date - The date to be formatted, either as a Date object or a string representation of a date.
 * @returns {string} The formatted date string in the format DD/MM/YYYY.
 *
 * @example
 * standarDateFormat(new Date('2022-01-01')); // Returns "01/01/2022"
 * standarDateFormat('2022-01-01'); // Returns "01/01/2022"
 */
export const standarDateFormat = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear().toString();
  const newDateString = `${day}/${month}/${year}`;

  return newDateString;
};

/**
 * Formats a Date object into a UTC string and adjusts for the timezone offset, ensuring the date and time values
 * remain consistent when sent to a backend that expects UTC dates.
 *
 * @function
 * @param {Date} date - The date to be formatted into a UTC string.
 * @returns {string} The formatted date string in UTC format.
 *
 * @example
 * const localDate = new Date('2022-01-01T12:00:00');
 * const utcString = formatUTCDate(localDate); // Returns a string in UTC format, e.g., "Sat, 01 Jan 2022 17:00:00 GMT"
 */
export function formatUTCDate(date: Date | null) {
  if (!date) {
    return null;
  }
  const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  const utcString = adjustedDate.toUTCString();
  return utcString;
}

export function formatTimeToHHMM(date: Date | null): string | null {
  if (!date) return null;
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function convertHHMMToDate(timeStr: string | null): Date | null {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;

  const now = new Date();
  now.setHours(hours, minutes, 0, 0);
  return now;
}

/**
 * Converts milliseconds to days, rounding down to the nearest integer.
 *
 * @function
 * @param {number} miliseconds - The number of milliseconds to convert.
 * @returns {number} The number of days.
 *
 * @example
 * convertMilisecondToDays(86400000); // Returns 1
 */
export const convertMilisecondToDays = (miliseconds: number): number => {
  const days = miliseconds / (1000 * 60 * 60 * 24);
  return Math.floor(days);
};

export const replaceEmptyArraysWithNull = (obj: any) => {
  if (typeof obj === 'object') {
    for (var key in obj) {
      if (Array.isArray(obj[key]) && obj[key].length === 0) {
        obj[key] = null;
      } else if (typeof obj[key] === 'object') {
        replaceEmptyArraysWithNull(obj[key]);
      }
    }
  }

  return obj;
};

/**
 * Determines if a date is earlier than the current date and time.
 * @param dateString The date string to compare in the format "YYYY-MM-DD".
 * @returns A boolean indicating whether the date is earlier than the current date and time.
 */
export const isDateYMDBeforeCurrent = (dateString: string): boolean => {
  const dateParts = dateString.split('-');
  const year = parseInt(dateParts[0]);
  const month = parseInt(dateParts[1]) - 1;
  const day = parseInt(dateParts[2]);
  const date = new Date(year, month, day);
  const currentDate = new Date();
  return date.getTime() < currentDate.getTime();
};

/**
 * Determines if a given date and time is earlier than the current date and time.
 * @param dateTimeString The date and time string to compare in the format "YYYY-MM-DDTHH:mm:ssZ".
 * @returns A boolean indicating whether the date and time have already passed.
 */
export const isDateTimeBeforeCurrent = (date: Date): boolean => {
  const currentDate = new Date();
  return date.getTime() < currentDate.getTime();
};

/**
 * Converts a string to a boolean.
 * If the string is 'true' (case insensitive), returns true.
 * If the string is 'false' (case insensitive), returns false.
 * If the string is anything else, or if the input is null, returns null.
 *
 * @param {'false' | 'true'} stringValue The string to convert.
 * @return {boolean | null} The converted boolean, or null if the input can't be converted.
 */
export const stringToBoolean = (stringValue: 'true' | 'false' | null | boolean): boolean => {
  if (stringValue === null) {
    return false;
  }

  if (typeof stringValue === 'string') {
    let lowerCaseString = stringValue.toLowerCase();

    if (lowerCaseString === 'true') {
      return true;
    } else if (lowerCaseString === 'false') {
      return false;
    }
  }

  // Handle cases where the value is neither 'true' nor 'false'.
  return false;
};

/**
 * Converts a boolean to a string.
 * If the boolean is true, returns 'true'.
 * If the boolean is false, returns 'false'.
 * If the input is null, returns null.
 *
 * @param {boolean} booleanValue The boolean to convert.
 * @return {string|null} The converted string, or null if the input is null.
 */
export const booleanToString = (booleanValue: boolean | null) => {
  if (booleanValue === null) {
    return null;
  }
  return booleanValue ? 'true' : 'false';
};
